/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useReducer, useEffect, useState, useRef, useCallback, useId } from 'react';
import cx from 'classnames';
import root from 'window-or-global';
import { certonaPageType } from '@/constants/certonaPageType';
import PartCard from '../PartCard';
import Image from '@/components/NextImage';
import azCommonStyles from '../../../theme/globals.module.scss';
import ContentContainer from '../ContentContainer';
import { defaultStoreUS } from '@/constants/locale';
import type { CertonaItem, AddToCartData } from '@/types';
import styles from './styles.module.scss';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { useUtagReady } from '@/utils/analytics/useUtagReady';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { FULFILLMENT_METHODS } from '../../../constants/fulfillmentConstants';
import { routePaths } from '../../../constants/routePaths';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import type { SkuAvailabilty } from '@/types/availability';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import { useMediaQuery } from '@/hooks/useMediaQuery';

type Props = {
  title: string;
  partsList: CertonaItem[];
  pageType: '' | string;
  greyBackground?: boolean;
  isFromExternal?: boolean;
  isCitrusCard?: boolean;
  onCertonaRecommendationClick?: () => void;
  addToCartAnalyticsandSuccessMessage?: (cartData: AddToCartData) => void;
  skuDetailsData?: SkuAvailabilty[] | null;
  cardsContainerCustomClass?: string;
  carouselId?: string;
};

type State = {
  componentListOfParts: any[];
};

const initialState: State = {
  componentListOfParts: [],
};

const carouselReducer = (state: State, action: { type: string; payload: any[] }) => {
  switch (action.type) {
    case 'setInitialList':
      return { ...state, componentListOfParts: action.payload };
    case 'leftClick':
      return { ...state, componentListOfParts: action.payload };
    case 'rightClick':
      return { ...state, componentListOfParts: action.payload };
    default:
      return state;
  }
};

const trackCertonaLinkInteractionBySection = (linkName: string, section: string, id: string) => {
  const utag = root.utag_data;
  const sectionName = `${section ?? utag?.pageType}_certona`;
  const data = {
    productLinkName: linkName,
    clickedProductId: id,
    pageName: utag?.pageName ?? '',
    pageType: utag?.pageType ?? '',
    siteSection: utag?.siteSection ?? '',
    eventType: sectionName.replace(/\s/g, '_').toLowerCase(),
  };
  clickTrack(data);
};

function PartsCarousel(props: Props) {
  const {
    title,
    partsList,
    pageType,
    isFromExternal,
    isCitrusCard = false,
    onCertonaRecommendationClick,
    addToCartAnalyticsandSuccessMessage,
    skuDetailsData,
    cardsContainerCustomClass,
    carouselId,
  } = props;
  const { isUtagReady: isUtagDefined } = useUtagReady();
  const certonaAddToCartEnabled = useCertonaAddToCartFlag({ ignorePageSpecificFlag: true });
  const [{ componentListOfParts }, dispatch] = useReducer(carouselReducer, initialState);
  const { data: headerData } = useHeaderData();
  const storeNumber = headerData?.storeNumber || defaultStoreUS;
  const router = useLegacyRouter();
  const { asPath } = router;

  const carouselRef = useRef<HTMLUListElement | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const isMobileOrTablet = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    dispatch({ type: 'setInitialList', payload: partsList });
  }, [partsList]);

  const skuNumbers = partsList.map((part) => part.id);

  const { data: pricingInfo } = useGetProductSkuPrice({
    skuNumbers,
    storeNumber,
    enabled: !!storeNumber,
    staleTime: 5 * 60000, // 5 minutes
  });

  const [keydown, setKeydown] = useState(false);

  const handlekeyPress = useCallback((event: MouseEvent | KeyboardEvent) => {
    if ('keyCode' in event && event?.keyCode === 9) {
      setKeydown(true);
    } else {
      setKeydown(false);
    }
  }, []);

  useEffect(() => {
    root?.document?.addEventListener('keydown', handlekeyPress);
    root?.document?.addEventListener('mousedown', handlekeyPress);
    return () => {
      root?.document?.removeEventListener('keydown', handlekeyPress);
      root?.document?.removeEventListener('mousedown', handlekeyPress);
    };
  }, [handlekeyPress]);

  const handleCertonaClick = (linkName: string, id: string) => {
    onCertonaRecommendationClick?.();
    root.sessionStorage?.setItem('productType', 'certona');
    if (isUtagDefined === undefined && pageType !== certonaPageType.shoppingCartPage) {
      trackCertonaLinkInteractionBySection(linkName, pageType, id);
    }
  };

  const handleRightClick = () => {
    const newListOfParts: any[] = [];
    componentListOfParts.forEach((_, index) => {
      if (index === 0) {
        newListOfParts.push(componentListOfParts[componentListOfParts.length - 1]);
      } else {
        newListOfParts.push(componentListOfParts[index - 1]);
      }
    });
    dispatch({ type: 'rightClick', payload: newListOfParts });

    const newCurrentSlide = currentSlide === 0 ? componentListOfParts.length - 1 : currentSlide - 1;
    setCurrentSlide(newCurrentSlide);
    setAnnouncement(
      isMobileOrTablet
        ? 'Moved to previous item'
        : `Moved to item ${newCurrentSlide + 1} of ${componentListOfParts.length}`,
    );
  };

  const handleLeftClick = () => {
    const newListOfParts = [];
    componentListOfParts.forEach((_, index) => {
      if (index !== 0) {
        newListOfParts.push(componentListOfParts[index]);
      }
    });
    newListOfParts.push(componentListOfParts[0]);
    dispatch({ type: 'leftClick', payload: newListOfParts });

    const newCurrentSlide = currentSlide === componentListOfParts.length - 1 ? 0 : currentSlide + 1;
    setCurrentSlide(newCurrentSlide);
    setAnnouncement(
      isMobileOrTablet
        ? 'Moved to next item'
        : `Moved to item ${newCurrentSlide + 1} of ${componentListOfParts.length}`,
    );
  };

  const handleCarouselKeyDown = (event: React.KeyboardEvent) => {
    if (componentListOfParts.length === 0 || isMobileOrTablet) {
      return;
    }
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        handleRightClick();
        break;
      case 'ArrowRight':
        event.preventDefault();
        handleLeftClick();
        break;
      case 'Home':
        event.preventDefault();
        setCurrentSlide(0);
        setAnnouncement(`Moved to first item of ${componentListOfParts.length}`);
        break;
      case 'End': {
        event.preventDefault();
        const lastIndex = componentListOfParts.length - 1;
        setCurrentSlide(lastIndex);
        setAnnouncement(`Moved to last item of ${componentListOfParts.length}`);
        break;
      }
    }
  };

  const reactId = useId();
  const carouselIdValue = carouselId || `parts-carousel-list-${reactId}`;
  const totalItems = componentListOfParts.length;
  const isNativeScroll = isMobileOrTablet;

  return (
    <section
      data-testid="carousel"
      className={styles.swipeableContainer}
      onKeyDown={handleCarouselKeyDown}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label={`${title} carousel with ${totalItems} items`}
    >
      <div aria-live="polite" aria-atomic="true" className={styles.srOnly}>
        {announcement}
      </div>

      <ContentContainer
        type="simple"
        withMargin
        className={`${styles.carousel} ${props.greyBackground ? styles.greyBackground : ''} ${
          !isCitrusCard ? styles.miniCartMargin : ''
        }
        `}
      >
        <h2
          className={cx(
            styles.partTitle,
            !isFromExternal && azCommonStyles['az-padding-bottom-4xs'],
            isFromExternal && styles.partTitleProductRecommendationCertona,
          )}
          data-testid="dynamic-content"
        >
          {title}
        </h2>
        {!isFromExternal && <div className={styles.separator} />}
        <div className={cx(styles.list_parts, isFromExternal ? styles.certonaCarousel : undefined)}>
          <button
            type="button"
            data-testid="carousel-left-arrow"
            className={cx(
              styles.leftArrow,
              isFromExternal ? styles.leftArrowRecommendation : undefined,
            )}
            onClick={handleRightClick}
            aria-label={`Previous items (currently viewing item ${currentSlide + 1} of ${totalItems})`}
            aria-controls={carouselIdValue}
            aria-describedby={isNativeScroll ? undefined : `${carouselIdValue}-status`}
          >
            <Image
              className={styles.chevron}
              src="/images/left-arrow-grey.svg"
              alt=""
              height={25}
              width={15}
              aria-hidden="true"
            />
          </button>
          <div
            className={styles.carouselContent}
            role="region"
            aria-label={
              isNativeScroll
                ? `Product list with ${totalItems} items`
                : `Product list showing item ${currentSlide + 1} of ${totalItems}`
            }
          >
            <ul
              ref={carouselRef}
              id={carouselIdValue}
              className={cx(styles.cards, cardsContainerCustomClass, styles.nativeScroll)}
              aria-live="polite"
              aria-atomic="false"
            >
              {componentListOfParts?.map(
                (
                  {
                    id,
                    product_url_relative,
                    product_image_url,
                    product_name,
                    rating,
                    total_review_count,
                    average_rating,
                    isQuotientCard,
                    citrusId,
                  },
                  idx,
                ) => {
                  const dynamicRef = carouselRef.current?.children[idx]?.children[0];
                  const skuDetails = skuDetailsData?.find((data: SkuAvailabilty) => {
                    return data?.skuId === id;
                  });
                  const bopusQuantity = skuDetails?.availabilityInfoVO?.bopusQuantity;
                  const sthQuantity = skuDetails?.availabilityInfoVO?.sthQuantity;

                  const fulFillmentId =
                    !!bopusQuantity && bopusQuantity > 0
                      ? FULFILLMENT_METHODS.STOREORDER
                      : !!sthQuantity && sthQuantity > 0
                        ? FULFILLMENT_METHODS.ONLINEORDER
                        : null;

                  const addToCartInfo = {
                    skuId: skuDetails?.skuId,
                    productId: skuDetails?.attributesInfoVO.productId,
                    vehicleId: skuDetails?.catalogVehicleId ? skuDetails.catalogVehicleId : '',
                    fulFillmentId: fulFillmentId,
                    brandName: skuDetails?.attributesInfoVO.brandName,
                  };
                  const showCard =
                    asPath === routePaths.cart &&
                    !isQuotientCard &&
                    certonaAddToCartEnabled &&
                    !isCitrusCard
                      ? (!!bopusQuantity && bopusQuantity > 0 && !!pricingInfo?.[id]?.totalPrice) ||
                        (!!sthQuantity && sthQuantity > 0 && !!pricingInfo?.[id]?.totalPrice)
                      : true;

                  return (
                    <PartCard
                      dynamicRef={dynamicRef as HTMLElement}
                      key={id}
                      aria-label={`${product_name} (item ${idx + 1} of ${totalItems})`}
                      part={{
                        properties: {
                          seoUrl: product_url_relative,
                          imageUrl: product_image_url,
                          refPageType: '',
                          makeModelYearPath: undefined,
                        },
                        label: product_name,
                        rating,
                        totalReviews: total_review_count,
                        averageRating: average_rating,
                        citrusId,
                      }}
                      pricingInfo={pricingInfo?.[id]}
                      addToCartData={
                        asPath === routePaths.cart && certonaAddToCartEnabled ? addToCartInfo : null
                      }
                      showCard={showCard}
                      actionHandler={handleCertonaClick}
                      isCertonaCard
                      isCitrusCard={isCitrusCard}
                      isFromExternal={isFromExternal}
                      onkeydown={keydown}
                      addToCartAnalyticsandSuccessMessage={addToCartAnalyticsandSuccessMessage}
                    />
                  );
                },
              )}
            </ul>
            {/* Carousel status for screen readers */}
            {!isNativeScroll && (
              <div
                id={`${carouselIdValue}-status`}
                aria-live="polite"
                aria-atomic="true"
                className={styles.srOnly}
              >
                {`Item ${currentSlide + 1} of ${totalItems}`}
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="carousel-right-arrow"
            className={`${styles.rightArrow} ${
              props.greyBackground ? styles.greyBgrightArrow : ''
            }`}
            onClick={handleLeftClick}
            aria-label={`Next items (currently viewing item ${currentSlide + 1} of ${totalItems})`}
            aria-controls={carouselIdValue}
            aria-describedby={isNativeScroll ? undefined : `${carouselIdValue}-status`}
          >
            <Image
              className={styles.chevron}
              src="/images/left-arrow-grey.svg"
              alt=""
              height={25}
              width={15}
              aria-hidden="true"
            />
          </button>
        </div>
      </ContentContainer>
    </section>
  );
}

export default PartsCarousel;
